import type { InstanceId } from '@cards/engine';

/**
 * Preserve the player's local hand order while removing cards that left the
 * hand and appending newly drawn cards in authoritative order.
 */
export function reconcileHandOrder(
  previous: readonly InstanceId[],
  current: readonly InstanceId[],
): readonly InstanceId[] {
  const currentSet = new Set(current);
  const retained = previous.filter((instanceId) => currentSet.has(instanceId));
  const retainedSet = new Set(retained);
  const next = [...retained, ...current.filter((instanceId) => !retainedSet.has(instanceId))];
  return next.length === previous.length && next.every((instanceId, index) => previous[index] === instanceId)
    ? previous
    : next;
}

/** Move a card to a physical hand slot, independent of the card occupying it. */
export function moveHandCardToIndex(
  order: readonly InstanceId[],
  instanceId: InstanceId,
  targetIndex: number,
): readonly InstanceId[] {
  if (!order.includes(instanceId) || !Number.isFinite(targetIndex)) return order;
  const without = order.filter((candidate) => candidate !== instanceId);
  const insertionIndex = Math.max(0, Math.min(Math.trunc(targetIndex), without.length));
  const next = [...without];
  next.splice(insertionIndex, 0, instanceId);
  return next.every((candidate, index) => order[index] === candidate) ? order : next;
}

export interface HandLayoutSlot {
  index: number;
  centerX: number;
  centerY: number;
}

/**
 * Pick a fixed layout slot. Geometry wins tie-breaks so a pointer on the seam
 * between wrapped rows cannot alternate based on which card currently owns a
 * slot.
 */
export function nearestHandSlotIndex(
  slots: readonly HandLayoutSlot[],
  clientX: number,
  clientY: number,
): number | undefined {
  let closest: (HandLayoutSlot & { distanceSquared: number }) | undefined;
  for (const slot of slots) {
    const dx = clientX - slot.centerX;
    const dy = clientY - slot.centerY;
    const distanceSquared = dx * dx + dy * dy;
    if (
      closest === undefined
      || distanceSquared < closest.distanceSquared
      || (
        distanceSquared === closest.distanceSquared
        && (slot.centerY < closest.centerY
          || (slot.centerY === closest.centerY && slot.centerX < closest.centerX))
      )
    ) {
      closest = { ...slot, distanceSquared };
    }
  }
  return closest?.index;
}
